import {
  FormControlLabel,
  Button,
  Checkbox,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { createUseStyles, useTheme } from "react-jss";
import { Filter } from "../application/Gallery";
import { CustomTheme } from "../application/App";

type RuleNames = "container" | "details" | "button";

interface FilterMenuProps {
  margin?: string;
  universes: string[];
  imageSizes: string[];
  finishes: string[];
  setFilters: Dispatch<SetStateAction<Filter>>;
}

const useStyles = createUseStyles<RuleNames, FilterMenuProps, CustomTheme>((theme) => ({
  container: (props) => ({ height: 0, margin: props.margin || 0 }),
  details: {
    display: "flex",
    flexDirection: "column",
  },
  button: { marginTop: "10px" },
}));

export const FilterMenu = (props: FilterMenuProps) => {
  const { setFilters } = props;

  const theme = useTheme<CustomTheme>();
  const classes = useStyles({ ...props, theme });

  const [selected, setSelected] = useState<Filter>({
    universes: [],
    imageSizes: [],
    finishes: [],
    isCommission: false,
  });

  const handleChange = (
    checked: boolean,
    type: keyof Omit<Filter, "isCommission">,
    value: string,
  ) => {
    if (checked) {
      setSelected({ ...selected, [type]: [...selected[type], value] });
    } else {
      setSelected({ ...selected, [type]: selected[type].filter((val) => val !== value) });
    }
  };

  const handleChangeCommission = (checked: boolean) => {
    setSelected({ ...selected, isCommission: checked });
  };

  const handleResetFilters = () => {
    setSelected({ universes: [], imageSizes: [], finishes: [], isCommission: false });
  };

  useEffect(() => {
    setFilters(selected);
  }, [selected, setFilters]);

  return (
    <div className={classes.container}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            Universe
            {selected.universes.length > 0 ? (
              <span>&nbsp;({selected.universes.length} selected)</span>
            ) : (
              ""
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          {props.universes.map((universe, index) => {
            return (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    color="secondary"
                    checked={selected.universes.includes(universe)}
                    onChange={(_, checked) => handleChange(checked, "universes", universe)}
                  />
                }
                label={universe.charAt(0).toUpperCase() + universe.slice(1)}
              />
            );
          })}
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            Image Size
            {selected.imageSizes.length > 0 ? (
              <span>&nbsp;({selected.imageSizes.length} selected)</span>
            ) : (
              ""
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          {props.imageSizes.map((imageSize, index) => {
            return (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    color="secondary"
                    checked={selected.imageSizes.includes(imageSize)}
                    onChange={(_, checked) => handleChange(checked, "imageSizes", imageSize)}
                  />
                }
                label={imageSize.charAt(0).toUpperCase() + imageSize.slice(1)}
              />
            );
          })}
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            Finish
            {selected.finishes.length > 0 ? (
              <span>&nbsp;({selected.finishes.length} selected)</span>
            ) : (
              ""
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          {props.finishes.map((finish, index) => {
            return (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    color="secondary"
                    checked={selected.finishes.includes(finish)}
                    onChange={(_, checked) => handleChange(checked, "finishes", finish)}
                  />
                }
                label={finish.charAt(0).toUpperCase() + finish.slice(1)}
              />
            );
          })}
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            Commission{selected.isCommission ? <span>&nbsp;(1 selected)</span> : ""}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                checked={selected.isCommission}
                onChange={(_, checked) => handleChangeCommission(checked)}
              />
            }
            label="Commission"
          />
        </AccordionDetails>
      </Accordion>
      <Button
        className={classes.button}
        variant="contained"
        color="secondary"
        onClick={handleResetFilters}
      >
        Reset filters
      </Button>
    </div>
  );
};
